// src/lib/money.ts — currencies, and the integer-cents arithmetic everything
// else in the app is built on.
//
// WHY CENTS
//
// Every amount in this app is stored as numeric(14,2) and every split has to
// add up exactly — trip_expense_splits_sum_check compares sums in the database
// and rejects the write if they disagree by a hundredth of a cent. Doing that
// arithmetic in JavaScript floats loses, eventually and quietly: 0.1 + 0.2 is
// 0.30000000000000004, and three shares of 113.33 sum to 339.99000000000007.
//
// So: convert to integer cents at the edge, do all arithmetic there, convert
// back only to display or to write. Nothing between those two points is a
// fractional number.

export interface Currency {
  code: string;
  symbol: string;
}

/**
 * The currencies offered in the picker, in the order they appear. CAD first
 * because it's home, CNY second because that's the trip; the rest are the
 * region and the usual suspects, matching scratch/tabi-trip-split.jsx.
 *
 * Note every currency here is treated as having two decimal places, including
 * JPY and KRW which conventionally have none. That is deliberate: the column
 * is numeric(14,2), the brief's rounding rule is stated in two decimals, and
 * a split rule that changed shape per currency is a bug farm. ¥1,200.00
 * displays a pair of zeroes that a Japanese price tag wouldn't. That is the
 * whole cost, and it buys one arithmetic path.
 */
export const CURRENCIES: Currency[] = [
  { code: 'CAD', symbol: '$' },
  { code: 'CNY', symbol: '¥' },
  { code: 'HKD', symbol: 'HK$' },
  { code: 'USD', symbol: 'US$' },
  { code: 'JPY', symbol: '¥' },
  { code: 'EUR', symbol: '€' },
  { code: 'GBP', symbol: '£' },
  { code: 'KRW', symbol: '₩' },
  { code: 'SGD', symbol: 'S$' },
  { code: 'THB', symbol: '฿' },
];

const BY_CODE = new Map(CURRENCIES.map((c) => [c.code, c]));

export function symbolOf(code: string): string {
  return BY_CODE.get(code)?.symbol ?? '';
}

/** Currency codes we know about, plus any the trip already uses. */
export function currencyOptions(...extra: (string | null | undefined)[]): Currency[] {
  const out = [...CURRENCIES];
  for (const code of extra) {
    if (code && !BY_CODE.has(code)) out.push({ code, symbol: '' });
  }
  return out;
}

/**
 * Anything money-shaped arriving from outside -> a number we can trust.
 *
 * PostgREST is expected to serialise `numeric` as a JSON number, and
 * src/data/types.ts types those columns as `number` on that basis. If it ever
 * hands back a string instead — a driver change, a future column typed
 * differently — a strict `Number.isFinite` check would reject every amount and
 * return 0, and the whole app would render $0.00 with no error anywhere. Every
 * balance would look settled.
 *
 * Silently wrong money is the worst failure this app has, so the two functions
 * every amount passes through coerce rather than assume.
 */
function toNumber(value: number | string): number {
  return typeof value === 'number' ? value : Number(value);
}

/**
 * Decimal amount -> integer cents.
 *
 * The `+ Number.EPSILON * ...` correction matters more than it looks. A value
 * like 8.115 arrives from JSON as 8.114999999999999, and a plain
 * Math.round(8.114999999999999 * 100) gives 811 where the decimal answer is
 * 812. Scaling the epsilon by the magnitude keeps the nudge proportional so it
 * cannot flip a value that genuinely sits below the halfway point.
 */
export function toCents(amount: number | string): number {
  const value = toNumber(amount);
  if (!Number.isFinite(value)) return 0;
  const scaled = value * 100;
  return Math.round(scaled + Math.sign(scaled) * Math.abs(scaled) * Number.EPSILON);
}

/** Integer cents -> decimal amount, for display and for writing to Postgres. */
export function fromCents(cents: number): number {
  return Math.round(cents) / 100;
}

/**
 * Parses what someone typed into an amount field. Accepts a leading currency
 * symbol, thousands separators and stray spaces, because people paste. Returns
 * null for anything it can't read, so callers can tell "empty" from "zero".
 */
export function parseAmount(input: string): number | null {
  const cleaned = input.replace(/[^0-9.,-]/g, '').replace(/,/g, '');
  if (cleaned === '' || cleaned === '-' || cleaned === '.') return null;
  const value = Number(cleaned);
  return Number.isFinite(value) ? value : null;
}

export interface MoneyOptions {
  /** Decimal places. Defaults to 2; pass 0 for headline totals. */
  decimals?: number;
  /** Include the currency symbol. Default true. */
  symbol?: boolean;
  /** Render the sign as a leading − / + rather than a minus on the digits. */
  signed?: boolean;
}

/**
 * Formats an amount for display. Always absolute-values the digits and puts
 * the sign in front of the symbol, so "−$84.20" reads as one unit rather than
 * "$-84.20".
 */
export function formatMoney(
  amount: number | string,
  code: string,
  options: MoneyOptions = {},
): string {
  const { decimals = 2, symbol = true, signed = false } = options;
  const parsed = toNumber(amount);

  // Anything under half a cent is zero. Without this a residual −0.000001 from
  // the balances view renders as "−$0.00", which reads as a debt.
  const value = !Number.isFinite(parsed) || Math.abs(parsed) < 0.005 ? 0 : parsed;
  const sign = signed && value > 0 ? '+' : value < 0 ? '−' : '';

  const digits = Math.abs(value).toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  return `${sign}${symbol ? symbolOf(code) : ''}${digits}`;
}
